/**
 * EPIC284 Task 6.4 — Current vs Recurring measurement-basis alignment.
 * When basis fields differ, do not imply comparability; surface a boundary.
 */

import type {
  OperationalMeasurementProvenance,
  SiteConditionMeasurementWindow,
} from './siteConditionState';
import { RECURRING_TRUSTWORTHY_HISTORY_NOTE } from './measurementContract';

export type RecurringHistoryTrustClass =
  /** Pre-vNext live accumulation — honest cold-start, not trustworthy history. */
  | 'building_pre_vnext'
  /** Post-drop / remint rebuild — must not read as trustworthy-history start (C2). */
  | 'provisional_rebuild'
  /** Reserved after episode definition vNext ships and calibration clock starts. */
  | 'trustworthy_post_vnext';

export type MeasurementBasisFingerprint = {
  scopeId: string | null;
  conditionId: string | null;
  conditionDefinitionVersion: string | null;
  episodeDefinitionVersion: string | null;
  denominatorPolicyVersion: string | null;
  inclusionPolicyVersion: string | null;
  /** Coverage eligibility / gate policy when declared on the window. */
  coverageEligibilityPolicy: string | null;
  /** Source record type (e.g. grouped_episodes vs raw_detections). */
  sourceRecordType: string | null;
  /** Period or window duration basis (e.g. P1D rolling vs PT15M operating). */
  periodDurationBasis: string | null;
};

export type MeasurementBasisDimension =
  | 'scopeId'
  | 'conditionId'
  | 'conditionDefinitionVersion'
  | 'episodeDefinitionVersion'
  | 'denominatorPolicyVersion'
  | 'inclusionPolicyVersion'
  | 'coverageEligibilityPolicy'
  | 'sourceRecordType'
  | 'periodDurationBasis';

export type MeasurementBasisAlignment = {
  comparable: boolean;
  mismatchedDimensions: MeasurementBasisDimension[];
  current: MeasurementBasisFingerprint;
  recurring: MeasurementBasisFingerprint;
  /** Customer-facing boundary copy when not comparable. */
  boundaryLabel: string | null;
  /** Inspectable "field: current → recurring" lines for details. */
  inspectableDiffs: string[];
};

const BASIS_DIMENSIONS: MeasurementBasisDimension[] = [
  'scopeId',
  'conditionId',
  'conditionDefinitionVersion',
  'episodeDefinitionVersion',
  'denominatorPolicyVersion',
  'inclusionPolicyVersion',
  'coverageEligibilityPolicy',
  'sourceRecordType',
  'periodDurationBasis',
];

const DIMENSION_LABELS: Record<MeasurementBasisDimension, string> = {
  scopeId: 'Scope',
  conditionId: 'Condition',
  conditionDefinitionVersion: 'Condition definition',
  episodeDefinitionVersion: 'Episode definition',
  denominatorPolicyVersion: 'Denominator policy',
  inclusionPolicyVersion: 'Inclusion policy',
  coverageEligibilityPolicy: 'Coverage eligibility',
  sourceRecordType: 'Source record type',
  periodDurationBasis: 'Period / window duration basis',
};

export const MEASUREMENT_BASIS_BOUNDARY_LABEL =
  'Measurement basis differs between Current and Recurring — histories are not comparable across this boundary.';

export type BasisWindowExtras = {
  coverageEligibilityPolicy?: string | null;
  sourceRecordType?: string | null;
};

function textOrNull(value: unknown): string | null {
  if (value == null) return null;
  const out = String(value).trim();
  return out || null;
}

/**
 * Declared measurement duration basis (type/version), not bucket resolution.
 * Current PT15M vs Recurring P1D resolutions differ by design and must not
 * alone create a Current↔Recurring boundary.
 */
function periodDurationBasisFromWindow(
  window: SiteConditionMeasurementWindow | null | undefined,
): string | null {
  if (!window) return null;
  const basisType = textOrNull(window.basis?.type);
  const basisVersion = textOrNull(window.basis?.version);
  const measurementEpoch = textOrNull(window.basis?.measurementEpoch);
  const parts = [basisType, basisVersion, measurementEpoch].filter(Boolean);
  return parts.length > 0 ? parts.join('|') : null;
}

export function measurementBasisFingerprintFromWindow(
  window: SiteConditionMeasurementWindow | null | undefined,
  extras?: BasisWindowExtras | null,
): MeasurementBasisFingerprint {
  const provenance: OperationalMeasurementProvenance | null | undefined =
    window?.operationalMeasurementProvenance;
  const coverage = window?.summary.measurementCoverage;
  return {
    scopeId: textOrNull(provenance?.scopeId),
    conditionId: textOrNull(provenance?.conditionId),
    conditionDefinitionVersion: textOrNull(provenance?.conditionDefinitionVersion),
    episodeDefinitionVersion: textOrNull(provenance?.episodeDefinitionVersion),
    denominatorPolicyVersion: textOrNull(provenance?.denominatorPolicyVersion),
    inclusionPolicyVersion: textOrNull(provenance?.inclusionPolicyVersion),
    coverageEligibilityPolicy: textOrNull(
      extras?.coverageEligibilityPolicy
      ?? coverage?.basisVersion
      ?? coverage?.basisId,
    ),
    sourceRecordType: textOrNull(extras?.sourceRecordType),
    periodDurationBasis: periodDurationBasisFromWindow(window),
  };
}

function bothPresentAndDiffer(
  left: string | null,
  right: string | null,
): boolean {
  return Boolean(left && right && left !== right);
}

/**
 * Compare Current vs Recurring basis. Missing fields on one side do not force a
 * mismatch (live K1 may omit optional extras); declared unequal values do.
 */
export function alignCurrentRecurringBasis(
  currentWindow: SiteConditionMeasurementWindow | null | undefined,
  recurringWindow: SiteConditionMeasurementWindow | null | undefined,
  extras?: {
    current?: BasisWindowExtras | null;
    recurring?: BasisWindowExtras | null;
  },
): MeasurementBasisAlignment {
  const current = measurementBasisFingerprintFromWindow(
    currentWindow,
    extras?.current,
  );
  const recurring = measurementBasisFingerprintFromWindow(
    recurringWindow,
    extras?.recurring,
  );
  const mismatchedDimensions = BASIS_DIMENSIONS.filter((dimension) => (
    bothPresentAndDiffer(current[dimension], recurring[dimension])
  ));
  const comparable = mismatchedDimensions.length === 0;
  const inspectableDiffs = mismatchedDimensions.map((dimension) => (
    `${DIMENSION_LABELS[dimension]}: ${current[dimension]} → ${recurring[dimension]}`
  ));
  return {
    comparable,
    mismatchedDimensions,
    current,
    recurring,
    boundaryLabel: comparable ? null : MEASUREMENT_BASIS_BOUNDARY_LABEL,
    inspectableDiffs,
  };
}

export function resolveRecurringHistoryTrustClass(params: {
  historyTrustClass?: RecurringHistoryTrustClass | null;
  isProvisionalRebuild?: boolean | null;
}): RecurringHistoryTrustClass {
  if (params.historyTrustClass) return params.historyTrustClass;
  if (params.isProvisionalRebuild) return 'provisional_rebuild';
  return 'building_pre_vnext';
}

export function recurringHistoryTrustNote(
  trustClass: RecurringHistoryTrustClass,
): string | null {
  if (trustClass === 'provisional_rebuild' || trustClass === 'building_pre_vnext') {
    return RECURRING_TRUSTWORTHY_HISTORY_NOTE;
  }
  return null;
}
